import React, { FC } from 'react';

import { isBrowser, I18nDataEdges } from '@lucid/utils';
import Cookies from 'js-cookie';
import {
  SiteNavbar,
  SiteNavbarOptionalProps,
} from '@lucid/components-react';

import { useMenuTopNavigation, useMenuEnterpriseNavbar, useMenuIntegrationPpcNavbar, useMenuSingleMarketoFormPageMe } from 'hooks/use-menu';
import useNavButtons, { placeholderButtons as initialPlaceholderButtons } from 'hooks/use-nav-buttons';

export interface NavbarOptionalProps extends SiteNavbarOptionalProps {
  /** Checks if the navbar is a discovery page. */
  isDiscovery?: boolean;
  /** Checks if the navbar is an enterprise page. */
  isEnterprise?: boolean;
  /** Checks if the navbar is an integration ppc page. */
  isIntegrationPpc?: boolean;
  /** Checks if the navbar is a single marketo form page. */
  isSingleMarketoFormPageMe?: boolean;
  /** The page type. */
  pageType?: string;
  /** Checks if we should hide the signup button. */
  shouldHideSignUpButton?: boolean;
  /** Checks if the navbar will use the contact sales cta. */
  willUseContactSalesCta?: boolean;
}

export interface NavbarProps extends NavbarOptionalProps {
  /** The i18n translation data. */
  i18nData: I18nDataEdges;
  /** The logo to be displayed in the Navbar. */
  logo: string | JSX.Element;
  /** The language currently set for the page context. */
  langPrefix: string;
  /** The Drupal Node ID */
  nid: string;
}

const Navbar: FC<NavbarProps> = ({
  i18nData,
  isDiscovery = false,
  isEnterprise = false,
  isIntegrationPpc = false,
  isSingleMarketoFormPageMe = false,
  isVisio = false,
  langPrefix,
  logo,
  nid,
  pageType,
  shouldHideSignUpButton,
  willUseContactSalesCta = false,
  ...rest
}) => {
  let shouldUseContactSalesCta = willUseContactSalesCta;
  const navbarMenu = useMenuTopNavigation(langPrefix);
  const enterpriseNavbarMenu = useMenuEnterpriseNavbar(langPrefix);
  const integrationPPCNavbarMenu = useMenuIntegrationPpcNavbar(langPrefix);
  const singleMarketoFormPageMenu = useMenuSingleMarketoFormPageMe(langPrefix);
  let chartNavbarMenu = navbarMenu;
  if (isEnterprise) {
    chartNavbarMenu = enterpriseNavbarMenu;
  }

  if (isIntegrationPpc) {
    chartNavbarMenu = integrationPPCNavbarMenu;
  }

  if (isSingleMarketoFormPageMe) {
    chartNavbarMenu = singleMarketoFormPageMenu;
  }
  // for now we are using addOn template to create some Cloud Insight pages.
  // These pages need a bit of customization for the nav so we don't show the pricing
  // link or "Sign up free" as the main nav CTA - this remove the pricing link
  // and change "Sign up free" into "Contact sales"
  const removeAddOnPricingLinkNids = ['4225'];
  if (pageType === 'addOn' && removeAddOnPricingLinkNids.includes(nid)) {
    shouldUseContactSalesCta = true;
    chartNavbarMenu = navbarMenu.filter(link => link.entityId !== '644');
  }

  const removeNineteenSolutionsPricingLinkNids = ['4172'];
  if (pageType === 'nineteenSolutions' && removeNineteenSolutionsPricingLinkNids.includes(nid)) {
    shouldUseContactSalesCta = true;
    chartNavbarMenu = navbarMenu.filter(link => link.entityId !== '644');
  }

  const username = isBrowser()
    ? window.atob(Cookies.get('username') || '')
    : undefined;
  const initialNavButtons = useNavButtons(
    username,
    isVisio,
    isDiscovery,
    shouldHideSignUpButton,
    shouldUseContactSalesCta
  );

  return (
    <SiteNavbar
      {...rest}
      i18nData={i18nData}
      initialNavButtons={initialNavButtons}
      initialPlaceholderButtons={initialPlaceholderButtons}
      logo={logo}
      menu={chartNavbarMenu}
    />
  );
};

export default Navbar;
